import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BasketItemComponentDef } from '@df/basket/view/basket-item.component.def';
import { IProductDataV3 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { ImageModule } from '@df/module-image/image.module';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { LocalBasket } from '../../basket/basket.decorator';
import { EAttrSet, Product } from '../../catalog/product/product.decorator';
import { SPECIFICATION_ATTRIBUTES_CODES } from '../../catalog/product/product.service';
import { ProductStockDescriptionService } from '../../catalog/product/stock-levels/product-stock-description.service';
import { ProductWishlistToggleComponent } from '../../catalog/product/wishlist-toggle/product-wishlist-toggle.component';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/api/result.component';
import { BasketItemProductSpecificationComponent } from '../item-product-specification/basket-item-product-specification.component';
import { BasketItemQtyComponent } from '../item-qty/basket-item-qty.component';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Basket {
        interface BasketItem {
            /**
             * True if product is added as a free gift
             */
            is_free_gift?: boolean;
            master_delivery_method_bs?: string | undefined;
            delivery_lead_time?: Date;
            use_additional_delivery_method?: 1 | 2;
        }
    }
}
@Component({
    selector: 'basket-view-item',
    templateUrl: './basket-view-item.component.html',
    styleUrl: './basket-view-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ActionComponent,
        BasketItemProductSpecificationComponent,
        BasketItemQtyComponent,
        CommonModule,
        FormsModule,
        ICmsClientModule,
        ImageModule,
        OptionLabelPipe,
        PriceComponent,
        ProductWishlistToggleComponent,
        ResultComponent,
        RouterLink,
        SizeClassDirective,
        TranslationModule
    ]
})
export class BasketViewItemComponent extends BasketItemComponentDef implements OnInit, OnChanges {
    /**
     * Hides qty selector and remove button for item.
     */
    @Input()
    lock?: boolean;

    override product?: Product<IProductDataV3>;
    private translationService = inject(TranslationService);
    private productResource = inject(ProductResource);
    private changeDetectorRef = inject(ChangeDetectorRef);

    productStockDescriptionService = inject(ProductStockDescriptionService);

    specificationAttributesCodes = SPECIFICATION_ATTRIBUTES_CODES;

    /**
     * Protection plan terms acceptance
     */
    protectionPlanTermsAccepted = false;

    /**
     * Stock warning
     */
    get warning(): string | undefined {
        if (!this.isSalable) {
            return this.maxQty
                ? this.translationService.transform('Only % available', this.maxQty)
                : this.translationService.transform('Out of stock');
        }
    }

    get isFreeGift(): boolean {
        return !!this.item?.data.is_free_gift;
    }

    /**
     * If item can be edited
     */
    get isEditable(): boolean {
        return !this.item?.data.is_free_gift && !this.lock;
    }
    get isProductProtectionPlan(): boolean {
        return !!this.product?.data?.isProductProtectionPlan;
    }

    get stockDescription(): string | undefined {
        return this.product && this.productStockDescriptionService.get(0, this.product.id);
    }

    get isGardenAssembly(): boolean {
        return this.product?.data?.attribute_set === EAttrSet.assemblyPlan;
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (this.product) {
            this.loadCoveredProducts();
        } else {
            setTimeout(() => {
                if (this.product) {
                    this.loadCoveredProducts();
                }
            }, 100);
        }
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['product'] && this.product) {
            if (this.isProductProtectionPlan) {
                this.loadCoveredProducts();
            }
        }
    }

    private loadCoveredProducts(): void {
        if (!this.product || !this.isProductProtectionPlan) {
            return;
        }

        const basket = this.app.customer.basket as LocalBasket;

        const productIds = basket.getProductsForPlan(this.product.id);

        if (productIds.length > 0) {
            this.productResource.loadAllIds(productIds, 3, { safe: true }).finally(() => {
                productIds.forEach(id => {
                    this.productResource.get(id);
                });
                this.changeDetectorRef.detectChanges();
            });
        }
    }

    get coveredProducts(): Product<IProductDataV3>[] {
        if (!this.product) return [];

        const basket = this.app.customer.basket as LocalBasket;
        const productIds = basket.getProductsForPlan(this.product.id);

        if (productIds.length === 0) return [];

        return this.productResource.getAllExisting(productIds) as Product<IProductDataV3>[];
    }

    asString(value: number | string): string {
        return value as string;
    }
}
